/**
 * The Zero Packing PC app's local recording link (SSD-7587), as its partner docs describe it for mini mode: plain text
 * POSTed to http://127.0.0.1:3000/ on the packing PC itself, so it only works from the browser on that PC.
 *
 * - A bare number starts a recording while the app waits, and stops it while the app records, whatever the number.
 *   The app keys the video by the number that started it, so the start is always the bare order id.
 * - `ZEROPACKING-STOP` only ever stops, and does nothing while the app waits. It is the only stop this screen sends.
 * - `GET /` answers `wait|` while the link is up. It says nothing about whether the app is recording.
 *
 * What the app (1.5.23) does with a signal, read from its own code: every answer carries
 * `Access-Control-Allow-Origin: *`, so this page can read it. 201 only means the HTTP server took the text and passed it
 * on; the app then starts nothing, without a word back, when it is not signed in, has no camera, is switching cameras,
 * its start policy blocks it, or its recorder fails to start. So the mini window is the proof of a recording.
 * A stop clears its recording flag at once (the file is finished in the background), so a number sent after an
 * accepted stop always lands as a start. The link runs only while mini mode is on. A scan typed into the mini
 * window's own box goes through the same toggle as a signal.
 *
 * A signal that got no answer may still have reached the app, so it is never sent again on its own: the screen shows
 * the state as unknown and the packer looks at the mini window.
 */
export const ZERO_PACKING_URL = 'http://127.0.0.1:3000/';
export const ZERO_PACKING_STOP = 'ZEROPACKING-STOP';
export const ZERO_PACKING_TIMEOUT_MS = 5000;
/** A start straight after a stop may land while the app is still closing the last video. */
export const ZERO_PACKING_STOP_GAP_MS = 700;

export type SignalAnswer =
  /** The app took the signal (201 Success). The recording itself is not confirmed by this. */
  | { kind: 'ACCEPTED'; status: number }
  /** The app answered and turned it down, so nothing started or stopped; 409 means the app needs an update. */
  | { kind: 'REFUSED'; status: number; text: string }
  /** No answer in time, the app is closed, or the browser blocked the call: it may or may not have arrived. */
  | { kind: 'NO_ANSWER'; message: string };

const failureText = (err: unknown): string => (err instanceof Error ? err.message : String(err));

/** Sends one signal and says what came back; never retries. */
export const sendZeroPackingSignal = async (
  body: string,
  timeoutMs: number = ZERO_PACKING_TIMEOUT_MS,
): Promise<SignalAnswer> => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(ZERO_PACKING_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
      body,
      signal: controller.signal,
    });
    if (response.ok) {
      return { kind: 'ACCEPTED', status: response.status };
    }
    const text = await response.text().catch(() => '');
    return { kind: 'REFUSED', status: response.status, text: text.trim() };
  } catch (err) {
    return {
      kind: 'NO_ANSWER',
      message: controller.signal.aborted ? `No answer in ${Math.round(timeoutMs / 1000)} s` : failureText(err),
    };
  } finally {
    clearTimeout(timer);
  }
};

/** Whether the app's link is up on this PC (mini mode on). It starts and stops nothing. */
export const probeZeroPacking = async (timeoutMs = 3000): Promise<boolean> => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(ZERO_PACKING_URL, { method: 'GET', signal: controller.signal });
    return response.ok && (await response.text()).trim().startsWith('wait');
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
};

/** What a failed signal tells the packer, in one line. */
export const describeSignalFailure = (answer: SignalAnswer): string => {
  if (answer.kind === 'REFUSED') {
    return answer.status === 409
      ? 'Zero Packing needs an update before it records again (409).'
      : `Zero Packing turned it down (${answer.status}${answer.text ? ` ${answer.text}` : ''}).`;
  }
  return answer.kind === 'NO_ANSWER' ? `No answer from Zero Packing: ${answer.message}.` : '';
};

/** Whether this PC records with Zero Packing; kept per browser, on by default. */
export const ZERO_PACKING_SETTING_KEY = 'ff.packing.zeroPacking.v1';

export const readZeroPackingEnabled = (): boolean => {
  try {
    return window.localStorage.getItem(ZERO_PACKING_SETTING_KEY) !== 'off';
  } catch {
    return true;
  }
};

export const writeZeroPackingEnabled = (enabled: boolean): void => {
  try {
    window.localStorage.setItem(ZERO_PACKING_SETTING_KEY, enabled ? 'on' : 'off');
  } catch {
    // A browser that refuses storage asks again on the next visit.
  }
};
